import { useState, useEffect } from "react";
import { Project } from "@/domain/models/project";
import { STORAGE_KEYS } from "@/domain/constants/storage";
import { projectRepository } from "@/domain/repositories/projectRepository";
import { storageService } from "@/application/services/storageService";

// 使用領域層的 projectRepository 處理所有 API 調用
// 使用應用層的 storageService 處理本地儲存

/**
 * 專案服務介面
 */
export interface ProjectServiceState {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
}

/**
 * 專案服務
 * 提供專案相關的操作和狀態管理
 */
export function useProjectService() {
  const [state, setState] = useState<ProjectServiceState>({
    projects: [],
    currentProject: null,
    isLoading: false,
    hasError: false,
  });

  // 載入專案列表
  const loadProjects = async () => {
    setState((prev) => ({ ...prev, isLoading: true, hasError: false }));

    try {
      // 使用 projectRepository 獲取專案列表
      const projects = await projectRepository.getProjects();

      setState((prev) => ({
        ...prev,
        projects,
        currentProject: projects.length > 0 ? projects[0] : null,
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        hasError: true,
        errorMessage: error instanceof Error ? error.message : "載入專案失敗",
      }));
    }
  };

  // 切換當前專案
  const switchProject = async (projectId: string) => {
    setState((prev) => ({ ...prev, isLoading: true, hasError: false }));

    try {
      // 使用 projectRepository 獲取專案詳情
      const selectedProject = await projectRepository.getProjectById(projectId);

      if (!selectedProject) {
        throw new Error("找不到指定的專案");
      }

      // 儲存當前選擇的專案到安全儲存
      storageService.setItem(
        STORAGE_KEYS.CURRENT_PROJECT_ID,
        String(projectId),
      );

      setState((prev) => ({
        ...prev,
        currentProject: selectedProject,
        isLoading: false,
      }));

      return selectedProject;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        hasError: true,
        errorMessage: error instanceof Error ? error.message : "切換專案失敗",
      }));
      throw error;
    }
  };

  // 初始化時載入專案列表並嘗試恢復上次選擇的專案
  useEffect(() => {
    const initializeProjects = async () => {
      await loadProjects();

      // 嘗試從本地存儲中恢復上次選擇的專案
      const savedProjectId = storageService.getItem(
        STORAGE_KEYS.CURRENT_PROJECT_ID,
      );
      if (savedProjectId && state.projects.length > 0) {
        const projectId = savedProjectId;
        const savedProject = state.projects.find((p) => p.id === projectId);

        if (savedProject) {
          setState((prev) => ({
            ...prev,
            currentProject: savedProject,
          }));
        }
      }
    };

    initializeProjects();
  }, []);

  return {
    ...state,
    loadProjects,
    switchProject,
  };
}
